import { Phone, WhatsappLogo, EnvelopeSimple } from "@phosphor-icons/react";
import { useI18n } from "@/lib/i18n";
import { SDRIVE_CONTACT } from "@/lib/contact";

/**
 * Prominent contact card shown in the Agency / Private Client portal so users always
 * have an obvious path to reach S.DRIVE directly.
 */
export default function SDriveContactCard({ compact = false }) {
  const { t } = useI18n();
  const wa = SDRIVE_CONTACT.phoneRaw.replace(/[^\d]/g, "");

  const actions = [
    {
      key: "call",
      href: `tel:${SDRIVE_CONTACT.phoneRaw}`,
      label: t("contact.call"),
      sub: SDRIVE_CONTACT.phone,
      Icon: Phone,
      tone: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/20",
    },
    {
      key: "whatsapp",
      href: `https://wa.me/${wa}`,
      external: true,
      label: t("contact.whatsapp"),
      sub: SDRIVE_CONTACT.phone,
      Icon: WhatsappLogo,
      tone: "bg-[#25D366]/10 text-[#25D366] border-[#25D366]/30 hover:bg-[#25D366]/20",
    },
    {
      key: "email",
      href: `mailto:${SDRIVE_CONTACT.email}`,
      label: t("contact.email"),
      sub: SDRIVE_CONTACT.email,
      Icon: EnvelopeSimple,
      tone: "bg-amber-500/10 text-amber-400 border-amber-500/30 hover:bg-amber-500/20",
    },
  ];

  return (
    <div
      data-testid="sdrive-contact-card"
      className={`rounded-lg border border-zinc-800 bg-[#121212] ${compact ? "p-4" : "p-5"}`}
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="w-7 h-7 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
          <Phone size={14} weight="fill" className="text-amber-500" />
        </div>
        <h3 className="text-sm font-medium text-white">{t("contact.title")}</h3>
      </div>
      <p className="text-xs text-zinc-400 mb-3 leading-relaxed">{t("contact.subtitle")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {actions.map(({ key, href, external, label, sub, Icon, tone }) => (
          <a
            key={key}
            data-testid={`contact-${key}`}
            href={href}
            {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
            className={`inline-flex items-center gap-2.5 rounded-md border px-3 py-2.5 transition-colors ${tone}`}
          >
            <Icon size={18} weight="fill" className="shrink-0" />
            <div className="min-w-0 text-left">
              <p className="text-[12px] font-semibold leading-none">{label}</p>
              <p className="text-[11px] opacity-80 truncate mt-1">{sub}</p>
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}
